import React from 'react';
import CalendarIcon from '@assets/icons/calendar.svg';
import BookmarkIcon from '@assets/icons/learning/bookmark.svg';
import BookmarkedIcon from '@assets/icons/learning/bookmarked.svg';
import { BaseTooltip } from '@components';
import { DATE_FORMATS } from '@constants/date.ts';
import { LEARNING_STATES } from '@constants/learningStates.ts';
import { BodySmSm, BodySmSmMedium, HeadingXxs } from '@constants/text.ts';
import { useAppSelector } from '@hooks/reduxHooks.ts';
import { useLingui } from '@lingui/react/macro';
import { ROUTES } from '@router/constants';
import { TooltipContent } from '@routes/___shared___/styled.tsx';
import { getLearningStatus } from '@routes/sidebar/___shared___/utils.ts';
import type { LearningResponse } from '@routes/sidebar/learning/api/learning.api.ts';
import {
    CardInfoBookmark,
    CardInfoDate,
    CardInfoTitle,
    CardInfoTop,
    CardInfoWrapper,
} from '@routes/sidebar/learning/ui/learning-tab/card-info/styled.tsx';
import { formatLearningDate } from '@routes/sidebar/learning/ui/learning-tab/card-info/utils.ts';
import { useCustomNavigate } from '@shared/hooks/useCustomNavigate';
import { truncateWithEllipsis } from '@utils/text.ts';

interface Props {
    learning: LearningResponse;
    onBookmarkClick: (learning: LearningResponse, e?: React.MouseEvent) => void;
    serverTime: Date;
}

const DESCRIPTION_MAX_LENGTH = 78;

const CardInfo: React.FC<Props> = ({ learning, onBookmarkClick, serverTime }) => {
    const { t } = useLingui();
    const navigate = useCustomNavigate();

    const { isCompleted, isOverdue, isDueSoon, isRefresher } = getLearningStatus(learning, serverTime);
    //get state when toggle bookmark
    const isBookmarkedId = useAppSelector((state) => state.learning.bookmarks.includes(learning.id));
    //get state when refresh page
    const isBookmarked = learning.userActivity?.states.some((s) => s === LEARNING_STATES.Bookmarked);
    const userInfo = useAppSelector((state) => state.user.userInfo);

    const dateFormat = userInfo?.preferences.dateFormat ?? DATE_FORMATS.ISO;
    const dueDate = formatLearningDate(learning.userActivity?.dueDateUTC, dateFormat);
    const completedDate = formatLearningDate(learning.userActivity?.completedAtUTC, dateFormat);

    //For recomended pathways/library there is no due/completed
    let dateInfo: { label: string; value: string } | null = null;

    const hasDueDate = dueDate !== null;

    if (isOverdue && dueDate) {
        dateInfo = { label: t`Due`, value: dueDate };
    } else if (isCompleted && isRefresher && hasDueDate) {
        dateInfo = { label: t`Valid until`, value: dueDate };
    } else if (completedDate !== null) {
        dateInfo = { label: t`Completed`, value: completedDate };
    } else if (hasDueDate) {
        dateInfo = { label: t`Due`, value: dueDate };
    }

    // The visual color state, made queryable for e2e (isOverdue/isDueSoon/isCompleted don't otherwise show up in the DOM).
    const dateStatus = isDueSoon
        ? 'due-soon'
        : isCompleted && !isOverdue
          ? 'completed'
          : isOverdue
            ? 'overdue'
            : 'default';

    const description = learning.learningDetails.shortDescription ?? '';
    const { truncatedDescription, isDescriptionTruncated } = truncateWithEllipsis(description, DESCRIPTION_MAX_LENGTH);

    return (
        <CardInfoWrapper>
            <CardInfoTop>
                <CardInfoDate
                    $isDueSoon={isDueSoon}
                    $isOverdue={isOverdue}
                    $isCompleted={isCompleted}
                    data-cy="learning-card-date"
                    data-status={dateStatus}
                >
                    {!!dateInfo && (
                        <>
                            <CalendarIcon />
                            <BodySmSmMedium>
                                {dateInfo.label}: {dateInfo.value}
                            </BodySmSmMedium>
                        </>
                    )}
                </CardInfoDate>
                {/* data-bookmarked lets e2e tests read the bookmark state (the icon alone does not show it in the DOM). */}
                <CardInfoBookmark
                    onClick={(e) => onBookmarkClick(learning, e)}
                    data-cy="learning-card-bookmark"
                    data-bookmarked={isBookmarked || isBookmarkedId}
                >
                    {isBookmarked || isBookmarkedId ? <BookmarkedIcon /> : <BookmarkIcon />}
                </CardInfoBookmark>
            </CardInfoTop>
            <CardInfoTitle
                onClick={() => {
                    navigate(`${ROUTES.SIDEBAR.LEARNING}/${learning.id}`);
                }}
            >
                <HeadingXxs data-cy="learning-card-title">{learning.learningDetails.title}</HeadingXxs>
                {isDescriptionTruncated ? (
                    <BaseTooltip title={<TooltipContent>{description}</TooltipContent>} arrow={false}>
                        <BodySmSm>{truncatedDescription}</BodySmSm>
                    </BaseTooltip>
                ) : (
                    <BodySmSm>{description}</BodySmSm>
                )}
            </CardInfoTitle>
        </CardInfoWrapper>
    );
};

export default CardInfo;
